import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { NotesModal, fmtDateTime, toLocalInput } from './shared';

const API = `${window.__BACKEND_URL__}/api`;

const ApproveModal = ({ token, amendment, onDone, onClose }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    clock_in: toLocalInput(amendment.requested_clock_in),
    clock_out: toLocalInput(amendment.requested_clock_out),
    notes: '',
  });
  const [busy, setBusy] = useState(false);

  const approve = async () => {
    if (!form.clock_in) {
      toast({ title: 'A clock-in time is required to approve', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      await axios.put(`${API}/time/amendments/${amendment.id}/review`, {
        action: 'approve',
        final_clock_in: new Date(form.clock_in).toISOString(),
        final_clock_out: form.clock_out ? new Date(form.clock_out).toISOString() : null,
        notes: form.notes || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Amendment approved', description: 'A supervisor-approved time entry was created.' });
      onDone();
      onClose();
    } catch (e) {
      toast({ title: 'Could not approve', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="amendment-approve-modal">
        <DialogHeader>
          <DialogTitle>Approve missing time — {amendment.user_name}</DialogTitle>
          <DialogDescription>Confirm or adjust the times. This creates a supervisor-approved time entry.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs italic rounded-lg px-3 py-2" style={{ background: 'rgba(255,200,0,0.08)', color: '#FBBF24', border: '1px solid rgba(255,200,0,0.25)' }}>
            Worker's reason: "{amendment.reason}"
          </p>
          <div>
            <Label className="text-xs">Clock-in</Label>
            <Input type="datetime-local" value={form.clock_in}
              onChange={(e) => setForm({ ...form, clock_in: e.target.value })}
              data-testid="amendment-approve-clockin" />
          </div>
          <div>
            <Label className="text-xs">Clock-out</Label>
            <Input type="datetime-local" value={form.clock_out}
              onChange={(e) => setForm({ ...form, clock_out: e.target.value })}
              data-testid="amendment-approve-clockout" />
          </div>
          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="e.g. Verified with site lead" data-testid="amendment-approve-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="amendment-approve-cancel">Cancel</Button>
          <Button onClick={approve} disabled={busy} className="bg-[#00C896] hover:bg-emerald-600 text-white" data-testid="amendment-approve-confirm">
            {busy ? 'Approving…' : '✅ Approve & Create Entry'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const AmendmentRequests = ({ token, onChanged }) => {
  const { toast } = useToast();
  const [amendments, setAmendments] = useState(null);
  const [approving, setApproving] = useState(null);
  const [denying, setDenying] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/time/amendments?status=pending`,
        { headers: { Authorization: `Bearer ${token}` } });
      setAmendments(data.amendments || []);
    } catch (e) { setAmendments([]); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const refreshAll = () => { load(); onChanged?.(); };

  const deny = async (reason) => {
    try {
      await axios.put(`${API}/time/amendments/${denying.id}/review`,
        { action: 'deny', notes: reason },
        { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Amendment denied', description: 'The worker has been notified.' });
      setDenying(null);
      refreshAll();
    } catch (e) {
      toast({ title: 'Could not deny', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  if (amendments === null) {
    return <div className="py-14 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: '#1E88E5' }} /></div>;
  }

  return (
    <div className="space-y-2" data-testid="amendment-requests-tab">
      {amendments.length === 0 ? (
        <div className="ht-card p-12 text-center">
          <p className="text-sm" style={{ color: '#64748B' }} data-testid="amendments-empty">No pending amendment requests.</p>
        </div>
      ) : amendments.map((a) => (
        <div key={a.id} className="ht-card p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`amendment-row-${a.id}`}>
          <div className="min-w-0">
            <p className="text-sm text-white font-semibold">{a.user_name}</p>
            <p className="text-xs mt-0.5" style={{ color: '#A0AEC0' }}>
              {a.requested_clock_in ? `In ${fmtDateTime(a.requested_clock_in)}` : 'In —'}
              {' · '}
              {a.requested_clock_out ? `Out ${fmtDateTime(a.requested_clock_out)}` : 'Out —'}
            </p>
            <p className="text-xs mt-1 italic" style={{ color: '#64748B' }}>"{a.reason}"</p>
            <p className="text-[11px] mt-1" style={{ color: '#4A5568' }}>Requested {fmtDateTime(a.created_at)}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button size="sm" onClick={() => setApproving(a)}
              className="bg-[#00C896] hover:bg-emerald-600 text-white font-semibold"
              data-testid={`amendment-approve-btn-${a.id}`}>
              ✅ Approve
            </Button>
            <Button size="sm" variant="outline" onClick={() => setDenying(a)}
              className="border-red-500/40 text-red-400 hover:bg-red-500/10"
              data-testid={`amendment-deny-btn-${a.id}`}>
              ❌ Deny
            </Button>
          </div>
        </div>
      ))}

      {approving && (
        <ApproveModal token={token} amendment={approving}
          onDone={refreshAll} onClose={() => setApproving(null)} />
      )}
      {denying && (
        <NotesModal title={`Deny ${denying.user_name}'s request`}
          description="The worker will see this reason on their time history."
          placeholder="e.g. Site cameras show you left at 3 PM — please resubmit with correct time"
          submitLabel="Deny Request" destructive
          onSubmit={deny} onClose={() => setDenying(null)} testId="deny-modal" />
      )}
    </div>
  );
};

export default AmendmentRequests;
